import { legacyToursSuppressed } from './legacyTourOwnership';
import { useEffect } from 'react';
import { launchInlineTour } from './launchInlineTour';
import { isTourSeen } from './tourSeen';

interface DrawerTourSession {
  defaultTab: 'note' | 'activity';
  editingActivity: unknown | null;
}

interface UseFirstOpenTourParams {
  tourId: string;
  isOpen: boolean;
  requireNote: boolean;
  /**
   * Snapshot del form (`useDrawerForm`'s `session`), NON lo stato `tab` corrente:
   * sul primo commit del rising edge di `isOpen`, `tab` non si è ancora
   * stabilizzato al valore di sessione (il reset gira in un effect di
   * `useDrawerForm` che, sullo STESSO passaggio, viene eseguito PRIMA di questo
   * hook — ma lo stato React aggiornato è visibile solo al render successivo).
   * `session.defaultTab` invece è un dato piatto, non racy: usarlo per decidere
   * lo switch evita la race del fix round 1 (vedi sotto).
   */
  session: DrawerTourSession | null;
  /** Lo STESSO setter usato dal tab switcher manuale (`DrawerHeader`/`form.setTab`):
   * l'editor TipTap è unico e condiviso tra i due tab, quindi lo switch non perde
   * mai il contenuto della nota già digitata. */
  setTab: (tab: 'note' | 'activity') => void;
}

/**
 * Trigger del tour "di novità" del drawer condiviso alla prima apertura utile.
 *
 * Fix round 1 (11/08): con `defaultTab='note'` (il caso maggioritario — "+ Nota",
 * Kanban, default ovunque) gli step `drawer-props`/`drawer-calendar` vivono SOLO
 * nel tab Attività, non erano mai montati nel tab Nota, e venivano scartati dal
 * filtro "step non montati" di `launchInlineTour`: il tour partiva con 3/5 step e
 * `onDestroyed` bruciava comunque il flag "visto" per sempre — due contenuti
 * chiave non venivano mai visti.
 *
 * Fix: se si parte dal tab Nota (e non si sta editando un'attività — lì il tab è
 * fisso e nascosto), si passa temporaneamente su Attività PRIMA di lanciare il
 * tour, e si torna su Nota SOLO alla chiusura effettiva del tour (`onFinished`,
 * chiamato da `launchInlineTour` dopo `markTourSeen`) o subito se il tour NON è
 * partito (`launchInlineTour` ritorna `false`: già visto, definizione assente,
 * zero step vivi). Il ripristino è ignorato (`cancelled`) se nel frattempo
 * l'effect è stato ripulito (drawer chiuso o nuova sessione) — evitando di
 * riportare su Nota un drawer già chiuso o riaperto per un'altra entità. Se
 * l'utente ha già cambiato tab manualmente durante il tour, il ripristino a
 * 'note' è un no-op innocuo (unico altro valore possibile).
 */
export function useFirstOpenTour({ tourId, isOpen, requireNote, session, setTab }: UseFirstOpenTourParams): void {
  useEffect(() => {
    if (legacyToursSuppressed() || !isOpen || !session || requireNote || isTourSeen(tourId)) return;

    const switchedForTour = session.defaultTab === 'note' && !session.editingActivity;
    if (switchedForTour) setTab('activity');

    let cancelled = false;
    const restoreTab = () => {
      if (!cancelled && switchedForTour) setTab('note');
    };

    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const launched = launchInlineTour(tourId, { onFinished: restoreTab });
      if (!launched) restoreTab();
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, session]);
}
